package medicine.components

import antd.button.button
import antd.comment.comment
import antd.descriptions.descriptions
import antd.descriptions.descriptionsItem
import antd.form.form
import antd.form.formItem
import antd.input.input
import antd.input.textArea
import antd.list.ListComponent
import antd.list.list
import antd.message.message
import antd.modal.modal
import antd.pageheader.pageHeader
import antd.tabs.tabPane
import antd.tabs.tabs
import com.jingfangpai.api.clients.MedicineApiClient
import com.jingfangpai.common.orm.mapping.Medicine
import com.jingfangpai.common.orm.mapping.Comment
import com.jingfangpai.common.orm.mapping.enums.CommentEntityType
import com.jingfangpai.common.orm.mapping.enums.CommentType
import com.jingfangpai.web.components.ImageManager
import external.toJsObject
import kotlinext.js.Object
import kotlinx.browser.window
import kotlinx.html.unsafe
import react.*
import react.dom.a
import react.dom.br
import react.dom.div
import react.dom.li
import react.dom.span
import react.router.dom.useHistory
import setTitle
import medicine.containers.MedicineDetailPageContext
import antd.avatar.avatar
import antd.card.card
import antd.icon.editOutlined
import antd.icon.userOutlined
import com.jingfangpai.common.data.model.IdName
import com.jingfangpai.common.data.type.DateTime
import com.jingfangpai.medicine.model.MedicineDetail
import kotlinext.js.js
import kotlinx.html.js.onClickFunction
import kotlinx.html.style
import react.dom.jsStyle
import react.dom.svg
import urlBase
import user.service.AuthService

external interface MedicineDetailProps : RProps {
    var id: String
}

val MedicineInDetail = functionalComponent<MedicineDetailProps> { props ->

    val ctx = useContext(MedicineDetailPageContext)
    val history = useHistory()
    val (medicine, setMedicine) = useState(MedicineDetail())
    val (notes, setNotes) = useState(arrayOf<Comment>())
    val (relatedPrescriptions, setRelatedPrescriptions) = useState(listOf<IdName>())
    val (medicineIdNames, setMedicineIdNames) = useState<Map<String, String>>(emptyMap())
    val (showNoteModal, setShowNoteModal) = useState(false)
    val (editingNote, setEditingNote) = useState<Comment?>(null)
    val (noteForm, setNoteForm) = useState(Comment())
    val (saving, setSaving) = useState(false)
    val (saveError, setSaveError) = useState<String?>(null)

    useEffect(props.id) {
        if (32 == props.id?.length ?: 0) {
            // Fetch all medicine ID names for clickable text
            MedicineApiClient.getIdNameList(
                okCallback = { response ->
                    if (response.ok() && response.data != null) {
                        setMedicineIdNames(response.data!!.filter { it.id != null }.associate { it.name!! to it.id!! })
                    }
                },
                errorCallback = { /* Silently fail */ }
            )
            
            ctx.getDetail(props.id, { resp ->
                if (resp.ok() && resp.data != null) {
                    setMedicine(resp.data!!)
                    setTitle(resp.data!!.name?.getLiteral() ?: "")
                    if(resp.data!!.name != null) {
                        // Fetch related prescriptions for this medicine
                        ctx.getRelatedPrescriptions(resp.data!!.name!!, { resp ->
                            if (resp.ok() && resp.data != null) {
                                setRelatedPrescriptions(resp.data!!)
                            }
                        }, { error ->
                            console.error("Failed to fetch related prescriptions:", error)
                        })
                    }
                } else {
                    console.error("Failed to fetch medicine detail:", resp)
                    message.error("获取中药失败: ${resp.message ?: "未知错误"}")
                }
            }, { error ->
                console.error("Exception fetching medicine detail:", error)
                console.error("Error stack:", error.printStackTrace())
                val errorMsg = error.message ?: error.toString() ?: "未知错误"
                message.error("获取中药失败: $errorMsg")
            })

            // Fetch notes for this medicine
            ctx.getComments(CommentEntityType.Medicine, props.id, CommentType.笔记, { resp ->
                if (resp.ok() && resp.data != null) {
                    setNotes(resp.data!!)
                }
            }, { error ->
                console.error("Failed to fetch notes:", error)
            })


        }
    }

    fun openAddNoteModal() {
        setEditingNote(null)
        setSaveError(null)
        setNoteForm(Comment().apply {
            entityType = CommentEntityType.Medicine
            entityId = props.id
            type = CommentType.笔记
            authorId = AuthService.getUserAuthData()?.id ?: ""
            createdBy = AuthService.getUserAuthData()?.userName ?: ""
            weight = 0
        })
        setShowNoteModal(true)
    }

    fun openEditNoteModal(note: Comment) {
        setEditingNote(note)
        setSaveError(null)
        setNoteForm(Object.assign(Comment(), note))
        setShowNoteModal(true)
    }

    fun saveNote() {
        if (noteForm.comment.isNullOrBlank()) {
            setSaveError("请填写笔记内容")
            return
        }

        // Ensure entityType is Medicine and set author info
        noteForm.entityType = CommentEntityType.Medicine
        noteForm.entityId = props.id
        noteForm.type = CommentType.笔记
        noteForm.authorId = AuthService.getUserAuthData()?.id ?: ""

        // Set createdOn timestamp for new notes
        if (noteForm.createdOn == null) {
            noteForm.createdOn = DateTime()
        }

        setSaving(true)
        setSaveError(null)
        ctx.saveComment(noteForm, { resp ->
            setSaving(false)
            if (resp.ok()) {
                // Refresh notes list
                ctx.getComments(CommentEntityType.Medicine, props.id, CommentType.笔记, { refreshResp ->
                    if (refreshResp.ok() && refreshResp.data != null) {
                        setNotes(refreshResp.data!!)
                    }
                    setShowNoteModal(false)
                    // Reset form after successful save
                    setNoteForm(Comment())
                    setEditingNote(null)
                }, {})
            } else {
                setSaveError("抱歉，出错了：${resp.message}")
            }
        }, {
            setSaving(false)
            console.log(it)
            setSaveError("保存失败，${it.message}")
        })
    }

    div("MedicineDetail") {
        attrs.jsStyle = kotlinext.js.js {
            margin = "0 auto"
            maxWidth = "1920px"
        }

        pageHeader {
            attrs {
                className = "medicine-detail-page-header-responsive"
                onBack = {
                    window.history.back()
                }
                val medicineName = medicine.name?.getLiteral() ?: ""
                val pinyin = medicine.pinyin
                val titleWithPinyin = if (!pinyin.isNullOrBlank()) {
                    "$medicineName ($pinyin)"
                } else {
                    medicineName
                }
                title = titleWithPinyin
                subTitle = ""
                if (!medicine.id.isNullOrBlank()) {
                    extra = arrayOf(buildElement {
                        button {
                            attrs {
                                onClick = {
                                    history.push(SmartTcmRoute.MedicineEdit.link.url.replace(":id", medicine.id?:""))
                                }
                            }
                            +"编辑"
                        }
                    })
                }
            }
        }

        div {
            // Image Manager Section
            ImageManager {
                attrs {
                    entityType = com.jingfangpai.common.orm.mapping.enums.FileEntityType.Medicine
                    entityId = props.id
                    onImagesChanged = { images ->
                        console.log("Medicine images changed:", images.size)
                    }
                }
            }
            
            // Basic Information Section
            card {
                attrs {
                    title = "基本信息"
                    style = kotlinext.js.js {
                        marginBottom = "16px"
                    }
                }

                descriptions {
                    attrs {
                        bordered = true
                        column = 2
                    }

                    val name = medicine.name?.getLiteral()
                    if (!name.isNullOrBlank()) {
                        descriptionsItem {
                            attrs {
                                label = "名称"
                                span = 2
                            }
                            +name
                        }
                    }

                    val nature = medicine.nature
                    if (!nature.isNullOrBlank()) {
                        descriptionsItem {
                            attrs {
                                label = "药性"
                                span = 2
                            }
                            +nature
                        }
                    }

                    val aliasName = medicine.aliasName
                    if (!aliasName.isNullOrBlank()) {
                        descriptionsItem {
                            attrs {
                                label = "别名"
                                span = 2
                            }
                            +aliasName
                        }
                    }

                    val type = medicine.type?.getLiteral()
                    if (!type.isNullOrBlank()) {
                        descriptionsItem {
                            attrs {
                                label = "类型"
                                span = 2
                            }
                            +type
                        }
                    }

                    val description = medicine.description
                    if (!description.isNullOrBlank()) {
                        descriptionsItem {
                            attrs {
                                label = "描述"
                                span = 2
                            }
                            ClickableMedicineText {
                                attrs.text = removeHtmlTags(description)
                                attrs.medicineIdNames = medicineIdNames
                                attrs.currentMedicineName = medicine.name?.getLiteral()
                            }
                        }
                    }

                    val differentiation = medicine.differentiation
                    if (!differentiation.isNullOrBlank()) {
                        descriptionsItem {
                            attrs {
                                label = "比较鉴别"
                                span = 2
                            }
                            ClickableMedicineText {
                                attrs.text = removeHtmlTags(differentiation)
                                attrs.medicineIdNames = medicineIdNames
                                attrs.currentMedicineName = medicine.name?.getLiteral()
                            }
                        }
                    }
                }
            }

            // Other Medicines of Same Type Section
            if (medicine.sameTypeOthers.isNotEmpty()) {
                card {
                    attrs {
                        title = "同类其他药物"
                        style = kotlinext.js.js {
                            marginBottom = "16px"
                        }
                    }
                    
                    div {
                        attrs.jsStyle = kotlinext.js.js {
                            display = "flex"
                            flexWrap = "wrap"
                            gap = "8px"
                        }
                        
                        medicine.sameTypeOthers.forEach { otherMedicine ->
                            button {
                                attrs {
                                    type = "link"
                                    style = kotlinext.js.js {
                                        padding = "4px 12px"
                                        fontSize = "14px"
                                    }
                                    onClick = {
                                        if (!otherMedicine.id.isNullOrBlank()) {
                                            history.push("$urlBase/learning/medicine/detail/${otherMedicine.id}")
                                        }
                                    }
                                }
                                +(otherMedicine.name?.getLiteral() ?: "")
                            }
                        }
                    }
                }
            }

            // Related Prescriptions Section (方剂应用)
            if (relatedPrescriptions.isNotEmpty()) {
                card {
                    attrs {
                        title = "方剂应用"
                        style = kotlinext.js.js {
                            marginBottom = "16px"
                        }
                    }
                    
                    div {
                        attrs.jsStyle = kotlinext.js.js {
                            display = "flex"
                            flexWrap = "wrap"
                            gap = "8px"
                        }
                        
                        relatedPrescriptions.forEach { prescription ->
                            button {
                                attrs {
                                    type = "link"
                                    style = kotlinext.js.js {
                                        padding = "4px 12px"
                                        fontSize = "14px"
                                    }
                                    onClick = {
                                        if (!prescription.id.isNullOrBlank()) {
                                            history.push("$urlBase/learning/prescription/detail/${prescription.id}")
                                        }
                                    }
                                }
                                +(prescription.name ?: "")
                            }
                        }
                    }
                }
            }

            // Notes Section
            card {
                attrs {
                    title = "笔记"
                }

                div {
                    button {
                        attrs {
                            type = "primary"
                            style = kotlinext.js.js {
                                marginBottom = "16px"
                            }
                            onClick = {
                                openAddNoteModal()
                            }
                        }
                        +"添加笔记"
                    }

                    if (notes.isEmpty()) {
                        div {
                            +"暂无笔记"
                        }
                    } else {
                        list<Comment, ListComponent<Comment>> {
                            attrs {
                                className = "comment-list"
                                header = "${notes.size}条笔记"
                                itemLayout = "horizontal"
                                dataSource = notes
                                renderItem = { noteItem, index ->
                                    buildElement {
                                        li {
                                            comment {
                                                attrs {
                                                    author = noteItem.createdBy

                                                    content = buildElement {
                                                        div {
                                                            if (!noteItem.title.isNullOrBlank()) {
                                                                div {
                                                                    +noteItem.title!!
                                                                    attrs.jsStyle = kotlinext.js.js {
                                                                        fontWeight = "bold"
                                                                        marginBottom = "8px"
                                                                        display = "block"
                                                                    }
                                                                }
                                                            }
                                                            div {
                                                                +(noteItem.comment ?: "")
                                                                attrs.jsStyle = kotlinext.js.js {
                                                                    whiteSpace = "pre-wrap"
                                                                }
                                                            }
                                                            if (!noteItem.description.isNullOrBlank()) {
                                                                div {
                                                                    +"（来源：${noteItem.description}）"
                                                                    attrs.jsStyle = kotlinext.js.js {
                                                                        marginTop = "8px"
                                                                        fontSize = "12px"
                                                                        color = "#666"
                                                                    }
                                                                }
                                                            }
                                                        }
                                                    }
                                                    datetime = noteItem.createdOn?.toDateTimeString()
                                                    actions = arrayOf(
                                                        buildElement {
                                                            button {
                                                                attrs {
                                                                    type = "link"
                                                                    size = "small"
                                                                    icon = buildElement {
                                                                        editOutlined {}
                                                                    }
                                                                    onClick = {
                                                                        openEditNoteModal(noteItem)
                                                                    }
                                                                }
                                                            }
                                                        }
                                                    )
                                                    attrs.avatar = buildElement {
                                                        avatar {
                                                            attrs {
                                                                style = kotlinext.js.js {
                                                                    backgroundColor = "#87d068"
                                                                }
                                                                shape = "square"
                                                                icon = buildElement {
                                                                    userOutlined {}
                                                                }
                                                            }
                                                        }
                                                    }
                                                }
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }

        // Note Modal
        modal {
            attrs {
                title = if (editingNote != null) "编辑笔记" else "添加笔记"
                visible = showNoteModal
                onCancel = {
                    if (!saving) {
                        setShowNoteModal(false)
                        // Reset form when closing modal
                        setNoteForm(Comment())
                        setEditingNote(null)
                        setSaveError(null)
                    }
                }
                onOk = {
                    saveNote()
                }
                okText = if (saving) "保存中..." else "保存"
                cancelText = "取消"
                okButtonProps = kotlinext.js.js {
                    loading = saving
                    disabled = saving
                }
            }

            form {
                attrs {
                    layout = "vertical"
                }

                // Show error message if any
                if (saveError != null) {
                    div {
                        attrs.style = kotlinext.js.js {
                            color = "#ff4d4f"
                            backgroundColor = "#fff2f0"
                            border = "1px solid #ffccc7"
                            padding = "8px 12px"
                            marginBottom = "16px"
                            borderRadius = "4px"
                        }
                        +saveError!!
                    }
                }

                formItem {
                    attrs {
                        label = "笔记标题"
                    }
                    input {
                        attrs {
                            value = noteForm.title ?: ""
                            placeholder = "请填写笔记标题（可选）"
                            disabled = saving
                            onChange = { event: dynamic ->
                                val newTitle = event.target.value as String
                                setNoteForm { currentNote ->
                                    Object.assign(Comment(), currentNote).apply {
                                        title = newTitle
                                    }
                                }
                            }

                        }
                    }
                }

                formItem {
                    attrs {
                        label = "笔记内容"
                    }
                    textArea {
                        attrs {
                            value = noteForm.comment ?: ""
                            rows = 8
                            placeholder = "请填写笔记内容"
                            disabled = saving
                            onChange = { event: dynamic ->
                                val newComment = event.target.value as String
                                setNoteForm { currentNote ->
                                    Object.assign(Comment(), currentNote).apply {
                                        comment = newComment
                                    }
                                }
                            }

                        }
                    }
                }

                formItem {
                    attrs {
                        label = "来源"
                    }
                    input {
                        attrs {
                            value = noteForm.description ?: ""
                            placeholder = "请填写笔记来源（可选）"
                            disabled = saving
                            onChange = { event: dynamic ->
                                val newDescription = event.target.value as String
                                setNoteForm { currentNote ->
                                    Object.assign(Comment(), currentNote).apply {
                                        description = newDescription
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
    }
}

// Pre-compiled regex for HTML tag removal (avoid creating on every call)
private val htmlTagRegex = Regex("<[^>]*>")

/**
 * Remove HTML tags from text
 */
fun removeHtmlTags(html: String): String {
    return html.replace(htmlTagRegex, "")
        .replace("&nbsp;", " ")
        .replace("&lt;", "<")
        .replace("&gt;", ">")
        .replace("&amp;", "&")
        .replace("&quot;", "\"")
        .replace("&#39;", "'")
        .trim()
}

/**
 * Pre-computed rendering data for a line in ClickableMedicineText.
 * Contains the full line and pre-resolved segments (with optional medicineId for clickable links).
 */
data class ClickableLineData(
    val lineText: String,
    val segments: List<LineSegmentData>
)

data class LineSegmentData(
    val start: Int,
    val end: Int,
    val medicineId: String?  // null = plain text, non-null = clickable medicine link
)

// Cache key to avoid recomputing spans when inputs haven't changed
data class SpanCacheKey(val text: String, val medicineIdNames: Map<String, String>, val currentMedicineName: String?)

private data class MedicineSpan(val start: Int, val end: Int, val medicineId: String)

// Module-level cache to prevent redundant computation across multiple component instances
private val computeClickableLinesCache = mutableMapOf<SpanCacheKey, List<ClickableLineData>>()
private const val MAX_CACHE_SIZE = 20

/**
 * Make medicine names in text clickable.
 * Pre-computes spans in useEffect to avoid blocking the main thread on every render.
 */
val ClickableMedicineText = functionalComponent<ClickableMedicineTextProps> { props ->
    val history = useHistory()
    val text = props.text
    val medicineIdNames = props.medicineIdNames
    val currentMedicineName = props.currentMedicineName

    // Pre-compute line data only when inputs change
    val (lineDataList, setLineDataList) = useState<List<ClickableLineData>>(emptyList())
    val (cacheKey, setCacheKey) = useState<SpanCacheKey?>(null)

    val newCacheKey = SpanCacheKey(text, medicineIdNames, currentMedicineName)
    useEffect(text, medicineIdNames, currentMedicineName) {
        if (cacheKey == null || cacheKey != newCacheKey) {
            setCacheKey(newCacheKey)
            // Check module-level cache before computing
            val cached = computeClickableLinesCache[newCacheKey]
            setLineDataList(cached ?: computeClickableLines(text, medicineIdNames, currentMedicineName))
        }
    }

    // Render from pre-computed data (cheap, just iterates line data)
    div {
        lineDataList.forEachIndexed { index, lineData ->
            if (index > 0) {
                br {}
            }
            lineData.segments.forEach { seg ->
                val segText = lineData.lineText.substring(seg.start, seg.end)
                if (seg.medicineId != null) {
                    a {
                        attrs.href = "#"
                        attrs.jsStyle = js {
                            color = "#1890ff"
                            textDecoration = "none"
                            fontWeight = "500"
                        }
                        attrs.onClickFunction = { event ->
                            event.preventDefault()
                            history.push("$urlBase/learning/medicine/detail/${seg.medicineId}")
                        }
                        +segText
                    }
                } else {
                    +segText
                }
            }
        }
    }
}

/**
 * Pre-compute clickable line data from text.
 * Uses pre-filtering and caching to avoid expensive computation.
 */
private fun computeClickableLines(
    text: String,
    medicineIdNames: Map<String, String>,
    currentMedicineName: String?
): List<ClickableLineData> {
    // Guard against empty text
    if (text.isBlank() || medicineIdNames.isEmpty()) {
        return emptyList()
    }

    // Pre-filter: only keep medicine names that actually appear in the text (case-insensitive)
    // This dramatically reduces the number of indexOf scans for long texts with many medicines
    val lowerText = text.lowercase()
    val relevantEntries = medicineIdNames.entries
        .filter { (name, _) -> name.isNotEmpty() && lowerText.contains(name.lowercase()) }

    if (relevantEntries.isEmpty()) {
        val result = text.split("\n").map { line ->
            ClickableLineData(line, listOf(LineSegmentData(0, line.length, null)))
        }
        cacheResult(SpanCacheKey(text, medicineIdNames, currentMedicineName), result)
        return result
    }

    // Sort by length descending to prefer longest match (e.g., "白附子" over "附子")
    val sortedEntries = relevantEntries.sortedByDescending { it.key.length }
    val spans = mutableListOf<MedicineSpan>()

    sortedEntries.forEach { (medicineName, medicineId) ->
        if (currentMedicineName != null && medicineName.equals(currentMedicineName, ignoreCase = true)) {
            return@forEach
        }
        var startIndex = 0
        while (true) {
            val index = text.indexOf(medicineName, startIndex, ignoreCase = true)
            if (index == -1) break
            val overlaps = spans.any { !(index >= it.end || index + medicineName.length <= it.start) }
            if (!overlaps) {
                spans.add(MedicineSpan(index, index + medicineName.length, medicineId))
            }
            startIndex = index + medicineName.length
        }
    }
    spans.sortBy { it.start }

    // Split text by newlines and associate spans to each line
    val lines = text.split("\n")
    val result = mutableListOf<ClickableLineData>()
    var offset = 0

    lines.forEach { line ->
        val lineEnd = offset + line.length
        val lineSegments = mutableListOf<LineSegmentData>()
        var lastPos = 0

        // Find spans that fall within this line
        val lineSpans = spans.filter { it.start >= offset && it.end <= lineEnd }

        lineSpans.forEach { span ->
            val relStart = span.start - offset
            val relEnd = span.end - offset

            // Add plain text before this span
            if (relStart > lastPos) {
                lineSegments.add(LineSegmentData(lastPos, relStart, null))
            }
            // Add clickable span
            lineSegments.add(LineSegmentData(relStart, relEnd, span.medicineId))
            lastPos = relEnd
        }

        // Add remaining plain text after last span
        if (lastPos < line.length) {
            lineSegments.add(LineSegmentData(lastPos, line.length, null))
        }

        result.add(ClickableLineData(line, lineSegments))
        offset = lineEnd + 1 // +1 for the newline character
    }

    cacheResult(SpanCacheKey(text, medicineIdNames, currentMedicineName), result)
    return result
}

/** Cache computation result with LRU-like eviction */
private fun cacheResult(key: SpanCacheKey, result: List<ClickableLineData>) {
    if (computeClickableLinesCache.size >= MAX_CACHE_SIZE) {
        computeClickableLinesCache.clear()
    }
    computeClickableLinesCache[key] = result
}

external interface ClickableMedicineTextProps : RProps {
    var text: String
    var medicineIdNames: Map<String, String>
    var currentMedicineName: String?
}
